// Schema do formulário de criação + conversão para o payload do POST.
//
// Espelha as validações do core (PaymentConfigurationRequest, FineRequest,
// InterestRequest): daysAfterDue obrigatório ≥ 0; pixAutoPreChargeDays 2..10
// opcional; description ≤ 255; multa/juros opcionais e, quando presentes, com
// daysAfterDue ≥ 0, amount > 0 e type MONETARY|PERCENTAGE.
//
// Os números ficam como TEXTO no form: campo vazio precisa existir (opcional),
// e o valor em reais/percentual é convertido por string — ver utils.ts.
import type { Path } from "react-hook-form"
import { z } from "zod"

import type {
  PaymentConfigurationAmountType,
  PaymentConfigurationCharge,
  PaymentConfigurationCreatePayload,
} from "@/types/payment-configurations"

import {
  parseChargeAmount,
  parseNonNegativeInteger,
  PERCENTAGE_MAX_HUNDREDTHS,
} from "./utils"

export const DESCRIPTION_MAX_LENGTH = 255
export const PIX_AUTO_PRE_CHARGE_MIN = 2
export const PIX_AUTO_PRE_CHARGE_MAX = 10

export const AMOUNT_TYPES = [
  "PERCENTAGE",
  "MONETARY",
] as const satisfies readonly PaymentConfigurationAmountType[]

export interface CreatePaymentConfigurationMessages {
  whiteLabelRequired: string
  descriptionTooLong: string
  daysRequired: string
  daysInvalid: string
  pixAutoRange: string
  amountRequired: string
  amountInvalid: string
  amountPositive: string
  percentageInvalid: string
  percentageMax: string
}

const chargeFieldsSchema = z.object({
  type: z.enum(AMOUNT_TYPES),
  amount: z.string(),
  daysAfterDue: z.string(),
})

/** Campo de dias opcional: vazio passa; preenchido precisa ser inteiro ≥ 0. */
const optionalDays = (message: string) =>
  z
    .string()
    .refine(
      (value) =>
        value.trim() === "" ||
        parseNonNegativeInteger(value) !== null,
      message
    )

// Valida uma regra (multa ou juros) SÓ quando ela está ligada.
const refineCharge = (
  charge: z.infer<typeof chargeFieldsSchema>,
  path: "fine" | "interest",
  context: z.RefinementCtx,
  messages: CreatePaymentConfigurationMessages
) => {
  if (charge.amount.trim() === "") {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: [path, "amount"],
      message: messages.amountRequired,
    })
  } else {
    const isPercentage = charge.type === "PERCENTAGE"
    const amount = parseChargeAmount(
      charge.amount,
      charge.type
    )
    if (amount === null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: [path, "amount"],
        message: isPercentage
          ? messages.percentageInvalid
          : messages.amountInvalid,
      })
    } else if (
      isPercentage &&
      amount > PERCENTAGE_MAX_HUNDREDTHS
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: [path, "amount"],
        message: messages.percentageMax,
      })
    } else if (amount <= 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: [path, "amount"],
        message: messages.amountPositive,
      })
    }
  }

  if (charge.daysAfterDue.trim() === "") {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: [path, "daysAfterDue"],
      message: messages.daysRequired,
    })
  } else if (
    parseNonNegativeInteger(charge.daysAfterDue) === null
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: [path, "daysAfterDue"],
      message: messages.daysInvalid,
    })
  }
}

export const buildCreatePaymentConfigurationSchema = (
  messages: CreatePaymentConfigurationMessages
) =>
  z
    .object({
      whiteLabelId: z
        .string()
        .trim()
        .min(1, messages.whiteLabelRequired),
      description: z
        .string()
        .max(
          DESCRIPTION_MAX_LENGTH,
          messages.descriptionTooLong
        ),
      daysDue: optionalDays(messages.daysInvalid),
      daysAfterDue: z
        .string()
        .refine(
          (value) => value.trim() !== "",
          messages.daysRequired
        )
        .refine(
          (value) =>
            value.trim() === "" ||
            parseNonNegativeInteger(value) !== null,
          messages.daysInvalid
        ),
      preChargeDays: optionalDays(messages.daysInvalid),
      pixAutoPreChargeDays: z.string().refine((value) => {
        if (value.trim() === "") return true
        const days = parseNonNegativeInteger(value)
        return (
          days !== null &&
          days >= PIX_AUTO_PRE_CHARGE_MIN &&
          days <= PIX_AUTO_PRE_CHARGE_MAX
        )
      }, messages.pixAutoRange),
      generateAllInvoices: z.boolean(),
      fineEnabled: z.boolean(),
      fine: chargeFieldsSchema,
      interestEnabled: z.boolean(),
      interest: chargeFieldsSchema,
    })
    .superRefine((values, context) => {
      if (values.fineEnabled) {
        refineCharge(values.fine, "fine", context, messages)
      }
      if (values.interestEnabled) {
        refineCharge(
          values.interest,
          "interest",
          context,
          messages
        )
      }
    })

export type CreatePaymentConfigurationValues = z.infer<
  ReturnType<typeof buildCreatePaymentConfigurationSchema>
>

const EMPTY_CHARGE: CreatePaymentConfigurationValues["fine"] =
  {
    type: "PERCENTAGE",
    amount: "",
    daysAfterDue: "",
  }

/** Valores iniciais — white label pré-escolhido quando há um só (ou o do filtro). */
export const createPaymentConfigurationDefaults = (
  whiteLabelId = ""
): CreatePaymentConfigurationValues => ({
  whiteLabelId,
  description: "",
  daysDue: "",
  daysAfterDue: "",
  preChargeDays: "",
  pixAutoPreChargeDays: "",
  generateAllInvoices: false,
  fineEnabled: false,
  fine: { ...EMPTY_CHARGE },
  interestEnabled: false,
  interest: { ...EMPTY_CHARGE },
})

/**
 * Ordem VISUAL dos campos — é ela que decide qual recebe o foco quando o
 * submit falha. A ordem das chaves do objeto de erros não é garantida.
 */
export const CREATE_FIELD_ORDER: ReadonlyArray<
  Path<CreatePaymentConfigurationValues>
> = [
  "whiteLabelId",
  "description",
  "daysDue",
  "daysAfterDue",
  "preChargeDays",
  "pixAutoPreChargeDays",
  "fine.amount",
  "fine.daysAfterDue",
  "interest.amount",
  "interest.daysAfterDue",
]

// Só chega aqui o que o schema já validou — o `?? 0` nunca é exercido.
const integerOf = (value: string) =>
  parseNonNegativeInteger(value) ?? 0

const optionalIntegerOf = (value: string) =>
  value.trim() === ""
    ? undefined
    : (parseNonNegativeInteger(value) ?? undefined)

const toCharge = (
  charge: CreatePaymentConfigurationValues["fine"]
): PaymentConfigurationCharge => ({
  type: charge.type,
  amount:
    parseChargeAmount(charge.amount, charge.type) ?? 0,
  daysAfterDue: integerOf(charge.daysAfterDue),
})

/**
 * Form validado → payload do POST: números inteiros, `amount` com 2 casas
 * implícitas, e campos opcionais vazios FORA do corpo (o core aplica o default).
 */
export const toCreatePaymentConfigurationPayload = (
  values: CreatePaymentConfigurationValues
): PaymentConfigurationCreatePayload => {
  const description = values.description.trim()
  const daysDue = optionalIntegerOf(values.daysDue)
  const preChargeDays = optionalIntegerOf(
    values.preChargeDays
  )
  const pixAutoPreChargeDays = optionalIntegerOf(
    values.pixAutoPreChargeDays
  )
  return {
    whiteLabelId: values.whiteLabelId.trim(),
    daysAfterDue: integerOf(values.daysAfterDue),
    generateAllInvoices: values.generateAllInvoices,
    ...(description ? { description } : {}),
    ...(daysDue !== undefined ? { daysDue } : {}),
    ...(preChargeDays !== undefined
      ? { preChargeDays }
      : {}),
    ...(pixAutoPreChargeDays !== undefined
      ? { pixAutoPreChargeDays }
      : {}),
    ...(values.fineEnabled
      ? { fine: toCharge(values.fine) }
      : {}),
    ...(values.interestEnabled
      ? { interest: toCharge(values.interest) }
      : {}),
  }
}

/**
 * Edição da descrição — o único campo editável (não entra no hash de
 * deduplicação do core; as regras seguem imutáveis). Vazia limpa o campo.
 */
export const buildDescriptionSchema = (
  descriptionTooLong: string
) =>
  z.object({
    description: z
      .string()
      .trim()
      .max(DESCRIPTION_MAX_LENGTH, descriptionTooLong),
  })

export type DescriptionFormValues = z.infer<
  ReturnType<typeof buildDescriptionSchema>
>
